import React from 'react'
import { TouchableOpacity, ActivityIndicator } from 'react-native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import moment from 'moment'
import { ActiveMinutes } from '@dialogue/services'
import { useTheme } from 'styled-components/native'
import styled from 'styled-components/native'

import { getColourScheme, ColourScheme } from 'APP/Helpers/AppearanceUtils'
import I18n, { isTranslationKey } from 'APP/I18n'
import Typography from 'APP/Converse/Typography'
import { Images } from 'APP/Themes'
import type { TrackerSectionProps, TrackerStatus } from './types'

const TrackerWrapper = styled.View<{ $disabled?: boolean }>`
  background-color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.contrastElementBg : theme.colors.elementsBg};
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.colors.elementsBorder};
`

const TrackerRow = styled.View`
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.baseMargin / 2}px
    ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const TrackerImage = styled.Image`
  width: 45px;
  height: 45px;
  margin-right: ${({ theme }) => theme.metrics.baseMargin * 0.25}px;
`

const TrackerName = styled(Typography).attrs({
  variant: 'subheaderBold',
})`
  flex: 1;
  padding: 0 ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const StyledTouchableOpacity = styled(TouchableOpacity)`
  padding: ${({ theme }) => theme.metrics.baseMargin}px 0;
`

const SubInfo = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.metrics.baseMargin * 1.25}px
    ${({ theme }) => theme.metrics.baseMargin * 0.6}px;
`

const TrackerSection: React.FC<TrackerSectionProps> = ({
  supported,
  deprecated,
  provider,
  status,
  disabled,
  modifying,
  last_synced_at,
  ctaStatusMap,
  onPress,
  temporaryDisabled,
}) => {
  const theme = useTheme()
  const providerKey = `HealthTrackerSettings.trackerProviders.${provider}`

  return (
    <TrackerWrapper $disabled={!supported || deprecated} testID="TRACKER">
      <TrackerRow>
        <TrackerImage
          source={Images.trackers[provider as keyof typeof Images.trackers]}
          resizeMode="cover"
        />
        <TrackerName>{isTranslationKey(providerKey) ? I18n.t(providerKey) : undefined}</TrackerName>
        {supported ? (
          <StyledTouchableOpacity
            testID={`TRACKER_CTA_${provider.toUpperCase()}`}
            disabled={disabled}
            onPress={onPress}
          >
            {modifying ? (
              <ActivityIndicator
                testID={`${provider.toUpperCase()}_MODIFYING`}
                color={theme.colors.accent}
              />
            ) : (
              <Typography variant="buttonText" color="accent">
                {(status && ctaStatusMap?.[status as TrackerStatus]) || ctaStatusMap?.default}
              </Typography>
            )}
          </StyledTouchableOpacity>
        ) : (
          <Typography variant="buttonText" color="disabled" testID="NOT_COMPATIBLE">
            {temporaryDisabled
              ? I18n.t('HealthTrackerSettings.ctas.temporaryDisabled')
              : I18n.t('HealthTrackerSettings.ctas.notCompatible')}
          </Typography>
        )}
      </TrackerRow>
      {deprecated ? (
        <SubInfo testID="TRACKER_DEPRECATED_INFO">
          <Typography variant="bodySmall">{I18n.t('HealthTrackerSettings.deprecated')}</Typography>
        </SubInfo>
      ) : (
        last_synced_at && (
          <SubInfo testID="TRACKER_SYNC">
            <Typography variant="bodySmall">
              {I18n.t('HealthTrackerSettings.synced')} {moment.utc(last_synced_at).fromNow()}
            </Typography>
            <MaterialIcon name="done" size={theme.metrics.icon.small} color={theme.colors.accent} />
          </SubInfo>
        )
      )}
      {status === ActiveMinutes.Types.TrackerStatus.Broken && (
        <SubInfo testID="TRACKER_RECONNECT">
          <Typography variant="bodySmall">{I18n.t('HealthTrackerSettings.reconnect')}</Typography>
          <MaterialIcon
            name="error"
            size={theme.metrics.icon.small}
            color={
              getColourScheme() === ColourScheme.DARK ? theme.colors.text : theme.colors.errorText
            }
          />
        </SubInfo>
      )}
    </TrackerWrapper>
  )
}

export default TrackerSection
